import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMarkAsReadStore } from '@lib/store';
import { useEventListener, useScrollLock } from '@shared/hook';
import { cn } from '@shared/utils';
import type { IFilter } from '../messages';
import type { GetMessagesResponse } from '@shared/api';
import { MessageItem } from './messageItem';
import MoreIcon from '@assets/icons/more.svg';

const COUNT_PER_PAGE = 10;

interface MessagesListProps {
  className?: string;
  messages: GetMessagesResponse[];
  filter: IFilter;
}

export const MessagesList = (props: MessagesListProps) => {
  const params = useParams<{ id: string; dir: 'inbox' | 'sent' }>();

  const { markAsRead } = useMarkAsReadStore();

  const [index, setIndex] = useState<number>(0);
  const [isLetterMode, setIsLetterMode] = useState(props.messages.some((message) => message.Id === params.id));
  const [isTablet, setIsTablet] = useState(window.outerWidth <= 768);

  useEventListener('resize', () => setIsTablet(window.outerWidth <= 768));

  useEffect(() => {
    setIsLetterMode(props.messages.some((message) => message.Id === params.id));
  }, [params.id, props.messages]);

  const { lock, unlock } = useScrollLock({ autoLock: false });
  useEffect(() => {
    if (isLetterMode && isTablet) lock();
    else unlock();
  }, [isLetterMode, isTablet]);

  const onShowMessage = useCallback((message: GetMessagesResponse) => {
    if (message.UnreadFlag) markAsRead(message.Id);
  }, []);

  const count = COUNT_PER_PAGE * (index + 1);
  const hasFilter = props.filter.searchQuery || props.filter.dateFilter.from || props.filter.dateFilter.to;
  return (
    <div className={cn(!!props.messages.length && 'page-animation', props.className)}>
      {hasFilter && !props.messages.length && props.filter.searchQuery && (
        <h3 className='mt-12 text-center text-sm font-bold text-[#929292] uppercase'>{`No results for: "${props.filter.searchQuery}"`}</h3>
      )}
      {hasFilter && !props.messages.length && !props.filter.searchQuery && (
        <h3 className='mt-12 text-center text-sm font-bold text-[#929292] uppercase'>No results found</h3>
      )}
      {!hasFilter && !props.messages.length && (
        <h3 className='mt-12 text-center text-sm font-bold text-[#929292] uppercase'>Message list is empty</h3>
      )}
      {props.messages.slice(0, count).map((message, idx) => {
        const isShown = params.id === message.Id;

        return (
          <MessageItem
            key={message.Id}
            isFirst={idx === 0}
            isLast={idx + 1 === props.messages.length}
            searchQuery={props.filter.searchQuery}
            message={message}
            isShown={isShown}
            redirect={params.dir!}
            scrollable={isTablet}
            onShowMessage={onShowMessage}
          />
        );
      })}
      {props.messages.length > count && (
        <div
          className='mx-auto flex w-fit cursor-pointer items-center gap-4 py-9 text-xs text-[#929292] uppercase'
          onClick={() => setIndex((prev) => prev + 1)}
        >
          <MoreIcon className='h-[30px] w-[30px]' />
          SHOW MORE
        </div>
      )}
    </div>
  );
};
